import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import { useAuth } from '../context/AuthContext';
import './Cart.css';

interface CartItem {
  productId: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    price: number; // Base price before discount
    stock: number;
    imageUrl?: string;
  };
}

interface CartData {
  items: CartItem[];
  subtotal?: string;
  discountPercent?: number;
  discountAmount?: string;
  total: string;
}

interface ShippingAddress {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface PreferredDelivery {
  date: string;
  timeSlot: string;
  instructions?: string;
}

const Cart: React.FC = () => {
  const [cart, setCart] = useState<CartData | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showMembershipPrompt, setShowMembershipPrompt] = useState(false);
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [fulfillmentType, setFulfillmentType] = useState<'delivery' | 'pickup'>('delivery');
  const [deliveryFee, setDeliveryFee] = useState<number | null>(null);
  const [deliveryEta, setDeliveryEta] = useState<string | null>(null);
  const [deliveryQuoteId, setDeliveryQuoteId] = useState<string | null>(null);
  const [deliveryQuoteLoading, setDeliveryQuoteLoading] = useState(false);
  const [deliveryQuoteError, setDeliveryQuoteError] = useState('');
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States',
  });
  const [preferredDelivery, setPreferredDelivery] = useState<PreferredDelivery>({
    date: '',
    timeSlot: '',
    instructions: '',
  });
  const [saveToProfile, setSaveToProfile] = useState(true);
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useSnackbar();
  const { user } = useAuth();
  const cartTotal = cart ? parseFloat(cart.total) : 0;
  const totalWithDelivery =
    cartTotal + (fulfillmentType === 'delivery' ? deliveryFee || 0 : 0);

  // Fetch cart and wallet balance only if user is logged in
  useEffect(() => {
    if (user) {
      fetchCart();
      fetchWalletBalance();
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Refresh wallet balance when page becomes visible (user might have topped up in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        fetchWalletBalance();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const fetchCart = async () => {
    try {
      const response = await api.get('/cart');
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await api.get('/wallet/balance');
      setWalletBalance(response.data.balance);
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/users/me');
      const profile = response.data.user;
      if (profile?.shippingAddress) {
        setShippingAddress({
          fullName: profile.shippingAddress.fullName || '',
          phone: profile.shippingAddress.phone || '',
          addressLine1: profile.shippingAddress.addressLine1 || '',
          addressLine2: profile.shippingAddress.addressLine2 || '',
          city: profile.shippingAddress.city || '',
          state: profile.shippingAddress.state || '',
          postalCode: profile.shippingAddress.postalCode || '',
          country: profile.shippingAddress.country || 'United States',
        });
      }
      if (profile?.preferredDelivery) {
        setPreferredDelivery({
          date: profile.preferredDelivery.date || '',
          timeSlot: profile.preferredDelivery.timeSlot || '',
          instructions: profile.preferredDelivery.instructions || '',
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchDeliveryQuote = useCallback(async () => {
    setDeliveryQuoteLoading(true);
    setDeliveryQuoteError('');
    try {
      const response = await api.post('/cart/delivery-quote', {
        shippingAddress,
        preferredDelivery,
      });
      setDeliveryFee(response.data.deliveryFee ?? null);
      setDeliveryEta(response.data.eta ?? null);
      setDeliveryQuoteId(response.data.quoteId ?? null);
    } catch (error: any) {
      setDeliveryFee(null);
      setDeliveryEta(null);
      setDeliveryQuoteId(null);
      setDeliveryQuoteError(error.response?.data?.error || 'Unable to fetch delivery estimate');
    } finally {
      setDeliveryQuoteLoading(false);
    }
  }, [preferredDelivery, shippingAddress]);

  useEffect(() => {
    if (fulfillmentType !== 'delivery') {
      setDeliveryFee(null);
      setDeliveryEta(null);
      setDeliveryQuoteId(null);
      setDeliveryQuoteError('');
      setDeliveryQuoteLoading(false);
      return;
    }

    const hasRequiredAddress =
      shippingAddress.fullName &&
      shippingAddress.phone &&
      shippingAddress.addressLine1 &&
      shippingAddress.city &&
      shippingAddress.state &&
      shippingAddress.postalCode &&
      shippingAddress.country;

    if (!hasRequiredAddress || !cart || cart.items.length === 0) {
      setDeliveryFee(null);
      setDeliveryEta(null);
      setDeliveryQuoteId(null);
      setDeliveryQuoteError('');
      setDeliveryQuoteLoading(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      fetchDeliveryQuote();
    }, 900);

    return () => window.clearTimeout(timeoutId);
  }, [cart, fetchDeliveryQuote, fulfillmentType, shippingAddress]);

  const updateQuantity = async (productId: string, quantity: number) => {
    try {
      await api.put('/cart/update', { productId, quantity });
      fetchCart();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to update cart');
    }
  };

  const removeItem = async (productId: string) => {
    try {
      await api.delete(`/cart/remove/${productId}`);
      fetchCart();
      showSuccess('Item removed from cart');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to remove item');
    }
  };

  const executeCheckout = async () => {
    if (!cart) return;
    if (!user) {
      showError('Please login or register to checkout');
      navigate('/login', { state: { returnTo: '/cart' } });
      return;
    }

    if (!cart || cart.items.length === 0) {
      showInfo('Your cart is empty');
      return;
    }

    // Check if user is a member
    if (!user.isMember) {
      setShowMembershipPrompt(true);
      return;
    }

    if (fulfillmentType === 'delivery') {
      // Validate shipping address
      if (
        !shippingAddress.fullName ||
        !shippingAddress.phone ||
        !shippingAddress.addressLine1 ||
        !shippingAddress.city ||
        !shippingAddress.state ||
        !shippingAddress.postalCode ||
        !shippingAddress.country
      ) {
        showError('Please complete your shipping address');
        return;
      }

      if (!preferredDelivery.date || !preferredDelivery.timeSlot) {
        showError('Please select a preferred delivery date and time');
        return;
      }

      if (deliveryFee === null || deliveryQuoteLoading) {
        showError('Please wait for the delivery estimate to load.');
        return;
      }
    }

    setCheckingOut(true);
    try {
      const response = await api.post('/cart/checkout', {
        fulfillmentType,
        shippingAddress,
        preferredDelivery,
        saveToProfile,
        deliveryQuoteId,
      });
      showSuccess(`Purchase successful! Remaining balance: $${response.data.purchase.remainingBalance}`);
      fetchCart();
      fetchWalletBalance(); // Refresh wallet balance
      navigate('/');
    } catch (error: any) {
      // Check if error is due to insufficient balance
      if (error.response?.data?.error?.includes('Insufficient')) {
        setShowInsufficientBalance(true);
      } else {
        showError(error.response?.data?.error || 'Checkout failed');
      }
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCheckout = async () => {
    if (!cart) return;

    if (!user) {
      showError('Please login or register to checkout');
      navigate('/login', { state: { returnTo: '/cart' } });
      return;
    }

    if (cart.items.length === 0) {
      showInfo('Your cart is empty');
      return;
    }

    // Check if user is a member
    if (!user.isMember) {
      setShowMembershipPrompt(true);
      return;
    }

    if (fulfillmentType === 'delivery') {
      // Validate shipping address
      if (
        !shippingAddress.fullName ||
        !shippingAddress.phone ||
        !shippingAddress.addressLine1 ||
        !shippingAddress.city ||
        !shippingAddress.state ||
        !shippingAddress.postalCode ||
        !shippingAddress.country
      ) {
        showError('Please complete your shipping address');
        return;
      }

      if (!preferredDelivery.date || !preferredDelivery.timeSlot) {
        showError('Please select a preferred delivery date and time');
        return;
      }
    }

    // Check wallet balance
    if (walletBalance !== null && walletBalance < totalWithDelivery) {
      setShowInsufficientBalance(true);
      return;
    }

    setShowPaymentConfirm(true);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading cart...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container">
        <div className="page-header">
          <h1>Shopping Cart</h1>
          <p>Review your items before checkout</p>
        </div>
        <div className="card empty-cart">
          <div className="empty-cart-icon">🔒</div>
          <h2>Login Required</h2>
          <p>Please login or register to view your cart and checkout</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
            <button className="btn btn-primary" onClick={() => navigate('/login', { state: { returnTo: '/cart' } })}>
              Login
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/register', { state: { returnTo: '/cart' } })}>
              Register
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="container">
        <div className="page-header">
          <h1>Shopping Cart</h1>
          <p>Review your items before checkout</p>
        </div>
        <div className="card empty-cart">
          <div className="empty-cart-icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Start adding items to your cart</p>
          <button className="btn btn-primary" onClick={() => navigate('/products')}>
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Shopping Cart</h1>
        <p>Review your items before checkout</p>
      </div>
      <div className="cart-container">
        <div className="cart-items">
          {cart.items.map((item) => (
            <div key={item.productId} className="cart-item">
              {item.product.imageUrl && (
                <div className="cart-item-image-container">
                  <img 
                    src={item.product.imageUrl} 
                    alt={item.product.name}
                    className="cart-item-image"
                  />
                </div>
              )}
              <div className="cart-item-info">
                <h3>{item.product.name}</h3>
                <div className="cart-item-pricing">
                  <span className="cart-item-price">
                    ${(item.product.price * (1 - (cart.discountPercent || 0) / 100)).toFixed(2)} each
                  </span>
                  {cart.discountPercent ? (
                    <span className="cart-item-discount">{cart.discountPercent}% member discount</span>
                  ) : null}
                </div>
                {(cart.discountPercent ?? 0) > 0 && (
                  <div className="cart-item-base-price">Base price ${item.product.price.toFixed(2)}</div>
                )}
              </div>
              <div className="cart-item-controls">
                <div className="quantity-controls">
                  <button
                    className="btn-quantity"
                    onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    -
                  </button>
                  <span className="quantity">{item.quantity}</span>
                  <button
                    className="btn-quantity"
                    onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                    disabled={item.quantity >= item.product.stock}
                  >
                    +
                  </button>
                </div>
                <div className="cart-item-total">
                  ${(item.product.price * (1 - (cart.discountPercent || 0) / 100) * item.quantity).toFixed(2)}
                </div>
                <button
                  className="btn btn-danger btn-small"
                  onClick={() => removeItem(item.productId)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="cart-summary">
          <div className="summary-card">
            <h2>Order Summary</h2>
            {(() => {
              const subtotal = parseFloat(cart.subtotal || cart.total);
              const discountAmount = parseFloat(cart.discountAmount || '0');
              return (
                <>
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {cart.discountPercent ? (
                    <div className="summary-row">
                      <span>Member discount ({cart.discountPercent}%):</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  ) : null}
                  {fulfillmentType === 'delivery' && (
                    <div className="summary-row">
                      <span>Delivery estimate:</span>
                      <span>
                        {deliveryQuoteLoading
                          ? 'Calculating...'
                          : deliveryFee !== null
                          ? `$${deliveryFee.toFixed(2)}`
                          : 'Unavailable'}
                      </span>
                    </div>
                  )}
                  {fulfillmentType === 'delivery' && deliveryEta && (
                    <div className="summary-row">
                      <span>Estimated delivery:</span>
                      <span>{new Date(deliveryEta).toLocaleString()}</span>
                    </div>
                  )}
                  {fulfillmentType === 'delivery' && deliveryQuoteError && (
                    <div className="summary-row">
                      <span className="error">{deliveryQuoteError}</span>
                    </div>
                  )}
                  <div className="summary-total">
                    <span>Total:</span>
                    <span>${totalWithDelivery.toFixed(2)}</span>
                  </div>
                  {walletBalance !== null && (
                    <div className="wallet-balance-info">
                      <div className="balance-row">
                        <span>Wallet Balance:</span>
                        <span className={walletBalance < totalWithDelivery ? 'balance-insufficient' : 'balance-amount'}>
                          ${walletBalance.toFixed(2)}
                        </span>
                      </div>
                      {walletBalance < totalWithDelivery ? (
                        <div className="insufficient-balance-warning">
                          <span className="warning-icon">⚠️</span>
                          <span>Insufficient balance. Top up to complete purchase.</span>
                        </div>
                      ) : walletBalance < totalWithDelivery * 1.5 && (
                        <div className="low-balance-notice">
                          <span className="notice-icon">💡</span>
                          <span>Low balance. Consider topping up for future purchases.</span>
                        </div>
                      )}
                      {walletBalance < totalWithDelivery && (
                        <Link to="/wallet" className="btn btn-primary btn-topup-cta">
                          Top Up Wallet Now
                        </Link>
                      )}
                    </div>
                  )}
                  <div className="shipping-section">
                    <h3>Fulfillment</h3>
                    <div className="fulfillment-options">
                      <label className={`fulfillment-option ${fulfillmentType === 'delivery' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="fulfillmentType"
                          value="delivery"
                          checked={fulfillmentType === 'delivery'}
                          onChange={() => setFulfillmentType('delivery')}
                        />
                        Delivery
                      </label>
                      <label className={`fulfillment-option ${fulfillmentType === 'pickup' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="fulfillmentType"
                          value="pickup"
                          checked={fulfillmentType === 'pickup'}
                          onChange={() => setFulfillmentType('pickup')}
                        />
                        Pickup
                      </label>
                    </div>

                    {fulfillmentType === 'delivery' ? (
                      <>
                        <div className="shipping-grid">
                          <div className="form-group">
                            <label>Full Name *</label>
                            <input
                              type="text"
                              value={shippingAddress.fullName}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, fullName: e.target.value })}
                              placeholder="Full name"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Phone *</label>
                            <input
                              type="tel"
                              value={shippingAddress.phone}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                              placeholder="Phone number"
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Address Line 1 *</label>
                          <input
                            type="text"
                            value={shippingAddress.addressLine1}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, addressLine1: e.target.value })}
                            placeholder="Street address"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Address Line 2 (Optional)</label>
                          <input
                            type="text"
                            value={shippingAddress.addressLine2 || ''}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, addressLine2: e.target.value })}
                            placeholder="Apartment, suite, etc."
                          />
                        </div>
                        <div className="shipping-grid">
                          <div className="form-group">
                            <label>City *</label>
                            <input
                              type="text"
                              value={shippingAddress.city}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                              placeholder="City"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>State *</label>
                            <input
                              type="text"
                              value={shippingAddress.state}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                              placeholder="State"
                              required
                            />
                          </div>
                        </div>
                        <div className="shipping-grid">
                          <div className="form-group">
                            <label>Postal / ZIP Code *</label>
                            <input
                              type="text"
                              value={shippingAddress.postalCode}
                              onChange={(e) =>
                                setShippingAddress({
                                  ...shippingAddress,
                                  postalCode: e.target.value.toUpperCase(),
                                })
                              }
                              placeholder="A1A 1A1 or 12345"
                              pattern="^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$"
                              title="Enter a valid postal code (e.g., A1A 1A1 or 12345)"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Country *</label>
                            <input
                              type="text"
                              value={shippingAddress.country}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                              placeholder="Country"
                              required
                            />
                          </div>
                        </div>
                        <div className="shipping-divider"></div>
                        <div className="shipping-grid">
                          <div className="form-group">
                            <label>Preferred Date *</label>
                            <input
                              type="date"
                              value={preferredDelivery.date}
                              min={new Date().toISOString().split('T')[0]}
                              onChange={(e) => setPreferredDelivery({ ...preferredDelivery, date: e.target.value })}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Time Slot *</label>
                            <select
                              value={preferredDelivery.timeSlot}
                              onChange={(e) => setPreferredDelivery({ ...preferredDelivery, timeSlot: e.target.value })}
                              required
                            >
                              <option value="">Select a time slot</option>
                              <option value="9:00 AM - 12:00 PM">9:00 AM - 12:00 PM</option>
                              <option value="12:00 PM - 3:00 PM">12:00 PM - 3:00 PM</option>
                              <option value="3:00 PM - 6:00 PM">3:00 PM - 6:00 PM</option>
                              <option value="6:00 PM - 9:00 PM">6:00 PM - 9:00 PM</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Delivery Instructions (Optional)</label>
                          <textarea
                            value={preferredDelivery.instructions || ''}
                            onChange={(e) => setPreferredDelivery({ ...preferredDelivery, instructions: e.target.value })}
                            placeholder="Gate code, leave at door, etc."
                            rows={3}
                          />
                        </div>
                        <label className="save-profile-toggle">
                          <input
                            type="checkbox"
                            checked={saveToProfile}
                            onChange={(e) => setSaveToProfile(e.target.checked)}
                          />
                          Save shipping details for future orders
                        </label>
                      </>
                    ) : (
                      <div className="pickup-info">
                        <p>Pickup orders are available at our main store location.</p>
                        <div className="pickup-details">
                          <div>📍 120 Market Street, Lagos</div>
                          <div>🕒 Pickup Hours: 9:00 AM - 7:00 PM</div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
            <button
              className="btn btn-primary btn-large"
              onClick={handleCheckout}
              disabled={
                checkingOut ||
                !user ||
                (fulfillmentType === 'delivery' && (deliveryQuoteLoading || deliveryFee === null)) ||
                (walletBalance !== null && walletBalance < totalWithDelivery)
              }
            >
              {checkingOut
                ? 'Processing...'
                : !user
                ? 'Login to Checkout'
                : fulfillmentType === 'delivery' && deliveryQuoteLoading
                ? 'Calculating delivery...'
                : fulfillmentType === 'delivery' && deliveryFee === null
                ? 'Delivery unavailable'
                : walletBalance !== null && walletBalance < totalWithDelivery
                ? 'Insufficient Balance'
                : 'Checkout'}
            </button>
          </div>
        </div>
      </div>

      {showPaymentConfirm && (
        <div className="membership-modal-overlay">
          <div className="payment-confirm-modal">
            <div className="modal-header">
              <h2>Confirm Purchase</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowPaymentConfirm(false)}
                aria-label="Close confirmation"
              >
                ×
              </button>
            </div>
            <p>
              You are about to pay <strong>${totalWithDelivery.toFixed(2)}</strong> from your wallet.
              Please confirm to proceed.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowPaymentConfirm(false);
                  executeCheckout();
                }}
                disabled={checkingOut}
              >
                Confirm Payment
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowPaymentConfirm(false)}
                disabled={checkingOut}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showMembershipPrompt && (
        <div className="membership-modal-overlay" onClick={() => setShowMembershipPrompt(false)}>
          <div className="membership-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Membership Required</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowMembershipPrompt(false)}
                aria-label="Close membership prompt"
              >
                ×
              </button>
            </div>
            <p>You need to be a member to make purchases. Register now to become a member and get $50 added to your wallet!</p>
            <div className="modal-actions">
              <Link to="/register" className="btn btn-primary" onClick={() => setShowMembershipPrompt(false)}>
                Become a Member
              </Link>
              <button className="btn btn-secondary" onClick={() => setShowMembershipPrompt(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showInsufficientBalance && cart && walletBalance !== null && (
        <div className="membership-modal-overlay" onClick={() => setShowInsufficientBalance(false)}>
          <div className="insufficient-balance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span />
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowInsufficientBalance(false)}
                aria-label="Close insufficient balance notice"
              >
                ×
              </button>
            </div>
            <div className="modal-icon">💰</div>
            <h2>Insufficient Wallet Balance</h2>
            <div className="balance-details">
              <div className="balance-detail-row">
                <span>Order Total:</span>
                <span className="amount">${totalWithDelivery.toFixed(2)}</span>
              </div>
              <div className="balance-detail-row">
                <span>Current Balance:</span>
                <span className="amount current">${walletBalance.toFixed(2)}</span>
              </div>
              <div className="balance-detail-row shortfall">
                <span>You Need:</span>
                <span className="amount shortfall-amount">
                  {(totalWithDelivery - walletBalance).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="topup-instructions">
              <p><strong>Top up your wallet to complete your purchase:</strong></p>
              <ol>
                <li>Go to your Wallet page</li>
                <li>Add funds in multiples of $500</li>
                <li>Return here to complete checkout</li>
              </ol>
            </div>
            <div className="modal-actions">
              <Link 
                to="/wallet" 
                className="btn btn-primary" 
                onClick={() => setShowInsufficientBalance(false)}
              >
                Go to Wallet
              </Link>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowInsufficientBalance(false);
                  fetchWalletBalance(); // Refresh balance in case user topped up in another tab
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;

